import React, { useState, useEffect, useRef } from "react";
import { Epg, Layout } from "planby";
import { useSelector } from "react-redux";

// Import hooks
import { useApp } from "./useAppLegacy";

// Import components
import { Timeline } from "./components";
import { ChannelItem } from "./components/ChannelItemLegacy";
import { ProgramItem } from "./components/ProgramItemLegacy";
import LiveDetailsModal from "./LiveDetailsModal";
import { ReactComponent as RightArrow } from "../../assets/Icons/rightArrow.svg";

function LiveEpgContainer({ selectedCategory }) {
  const { isLoading, getEpgProps, getLayoutProps, channelLength, channels, onScrollLeft } = useApp({ selectedCategory });
  const [filteredChannels, setFilteredChannels] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedShow, setSelectedShow] = useState(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  
  const containerRef = useRef(null);
  const scrollableRef = useRef(null);
  const timelineRef = useRef(null);
  const scrollCheckIntervalRef = useRef(null);
  
  const projectInfo = useSelector((state) => state?.projectInfo?.value);
  const pubid = projectInfo?.projectConfig?.pubid;
  
  const scrollTimeline = (direction) => {
    if (!scrollableRef.current) return;
    if (screenWidth <= 480) return;
    
    const scrollAmount = direction === 'left' 
      ? -Math.min(scrollableRef.current.clientWidth * 0.5, 600)
      : Math.min(scrollableRef.current.clientWidth * 0.5, 1200);
    
    scrollableRef.current.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
    
    setTimeout(checkScrollPosition, 400);
  };
  
  const checkScrollPosition = () => {
    if (!scrollableRef.current) return;
    if (screenWidth <= 480) {
      setShowLeftArrow(false);
      setShowRightArrow(false);
      return;
    }
    
    const { scrollLeft, scrollWidth, clientWidth } = scrollableRef.current;
    const maxScroll = scrollWidth - clientWidth;
    
    setShowLeftArrow(scrollLeft > 5);
    
    setShowRightArrow(maxScroll > 0 && scrollLeft < maxScroll - 5);
  };
  
  const findScrollableElement = () => {
    if (!containerRef.current) return;
    
    const selectors = [
      '.planby [class*="TimelineScroll"]',
      '.planby [class*="ScrollBox"]',
      '.css-tv2j2u-ScrollBox',
      '.planby [style*="overflow-x: auto"]',
      '.planby [style*="overflow-x:auto"]',
      '.planby [style*="overflow: auto"]',
      '.planby-epg-container > div > div'
    ];
    
    for (const selector of selectors) {
      const elements = containerRef.current.querySelectorAll(selector);
      
      for (const element of elements) {
        if (element && element.scrollWidth > element.clientWidth) {
          scrollableRef.current = element;
          
          element.addEventListener('scroll', checkScrollPosition);
          
          checkScrollPosition();
          return true;
        }
      }
    }
    
    const deepFind = (element) => {
      if (!element) return false;
      
      if (element.scrollWidth > element.clientWidth && 
          (getComputedStyle(element).overflowX === 'auto' || 
           getComputedStyle(element).overflowX === 'scroll')) {
        scrollableRef.current = element;
        element.addEventListener('scroll', checkScrollPosition);
        checkScrollPosition();
        return true;
      }
      
      for (let i = 0; i < element.children.length; i++) {
        if (deepFind(element.children[i])) {
          return true;
        }
      }
      
      return false;
    };
    
    return deepFind(containerRef.current);
  };

  useEffect(() => {
    if (isLoading) return;
    if (screenWidth <= 480) return;
    
    let attempts = 0;
    const maxAttempts = 10;
    
    const findScrollableInterval = setInterval(() => {
      if (attempts >= maxAttempts || findScrollableElement()) {
        clearInterval(findScrollableInterval);
      }
      attempts++;
    }, 300);
    
    scrollCheckIntervalRef.current = setInterval(checkScrollPosition, 1000);
    
    return () => {
      clearInterval(findScrollableInterval);
      if (scrollCheckIntervalRef.current) {
        clearInterval(scrollCheckIntervalRef.current);
      }
      
      if (scrollableRef.current) {
        scrollableRef.current.removeEventListener('scroll', checkScrollPosition);
      }
    };
  }, [isLoading, pubid, screenWidth]);
  
  useEffect(() => {
    if (!isLoading) {
      setTimeout(() => {
        if (!scrollableRef.current) {
          findScrollableElement();
        }
        checkScrollPosition();
      }, 500);
    }
  }, [isLoading, filteredChannels, screenWidth, pubid]);
  
  useEffect(() => {
    if (!isLoading && onScrollLeft) {
      onScrollLeft();
      
      if (screenWidth > 480) {
        window.epgScrollLeft = () => scrollTimeline('left');
        window.epgScrollRight = () => scrollTimeline('right');
        window.forceEpgScroll = (direction) => scrollTimeline(direction);
      }
      
      setTimeout(() => {
        checkScrollPosition();
      }, 1000);
    }
    
    return () => {
      window.epgScrollLeft = null;
      window.epgScrollRight = null;
      window.forceEpgScroll = null;
    };
  }, [isLoading, onScrollLeft, pubid, screenWidth]);
  
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  useEffect(() => {
    if (selectedCategory) {
      const updatedChannels = channels.filter(channel =>
        channel?.categories?.some(category => category?.category_id === selectedCategory?.category_id)
      );
      setFilteredChannels(updatedChannels);
    } else {
      setFilteredChannels(channels);
    }
    
    
  }, [selectedCategory, channels]);
  
  const handleShowClick = (showData) => {
    setSelectedShow(showData);
    setShowModal(true);
  };

  const showArrows = pubid?.toString() !== "50120" && screenWidth > 480;

  return (
    <div className="liveEpgContainerLegacy">
      <div className="epg-container-with-controls">
        {showArrows && (
          <button
            id="epg-scroll-left-btn"
            onClick={() => scrollTimeline('left')}
            className={`arrow-btn left epg-arrow-btn show${!showLeftArrow ? ' disabled' : ''}`}
            disabled={!showLeftArrow}
          >
            <span><RightArrow /></span>
          </button>
        )}
        
        <div className={`epg-main-container ${"kalingo"}`} style={{ width: "calc(100% - 80px)" }}>
          <div 
            ref={containerRef}
            style={{ 
              height: pubid?.toString() !== "50120" ? "80vh" : channelLength <= 9 ? "100vh" : "190vh", 
              width: "100%",
              position: 'relative' 
            }}
            id="epg-outer-container"
            className="epg-wrapper"
          >
            <Epg 
              isLoading={isLoading} 
              isInitialScrollToNow={true} 
              initialScrollOffsetMinutes={-660} 
              className="planby-epg-container"
              {...getEpgProps()}
            >
          <Layout
            {...getLayoutProps({ channels: filteredChannels })}
            renderTimeline={(props) => <Timeline {...props} />}
            renderProgram={({ program, ...rest }) => (
              <ProgramItem key={program.data.id} program={program} {...rest} onShowClick={handleShowClick} />
            )}
            renderChannel={({ channel }) => (
              <ChannelItem channel={channel} />
            )}
          />
        </Epg>
      </div>
        </div>

        {showArrows && (
          <button
            id="epg-scroll-right-btn"
            onClick={() => scrollTimeline('right')}
            className={`arrow-btn right epg-arrow-btn show${!showRightArrow ? ' disabled' : ''}`}
            disabled={!showRightArrow}
          >
            <span><RightArrow /></span>
          </button>
        )}
      </div>
      {showModal && (
        <LiveDetailsModal show={selectedShow} onClose={() => setShowModal(false)} />
      )}
    </div>
  );
}

export default LiveEpgContainer;
